/**
 * Onda Fix 4 (frente 2) — pure formatting for the sea-HUD carry indicators.
 * The client already knows everything it needs with NO new wire: `self.chestCount` (chests aboard, 0..hull
 * cap) rides the snapshot self-header, and the materials hold arrives via S_GOODS (`net.goods.hold`). The rolled
 * chest VALUES are deliberately server-only (revealed at the sale) — so the chest chip is a count, not a value.
 * DOM-free on purpose: main.ts/hud.ts render these strings, tests lock them.
 */
import { materialById, type GoodsEntry } from '@piratas/protocol';
import { itemIconText } from './itemicon.js';

/** Tap-toast for the 💰 chest chip — where to turn the flag into gold. */
export const CHEST_CHIP_HINT = '💰 Treasure chest aboard — sail into an outpost ring to sell it.';

/** Tap-toast for the 📦 cargo chip — unbanked loot gold and its risk. */
export const CARGO_CHIP_HINT = '📦 Unbanked loot — berth at your home dock to bank it. Sink and you drop it!';

/** Tap-toast for the 🧺 hold chip when the hold is empty (shouldn't show, but belt-and-braces). */
export const HOLD_CHIP_EMPTY = '🧺 Hold empty — fish, dive wrecks and run storms to fill it.';

/** How many materials the hold-chip toast lists before folding the rest into "+N more". */
export const HOLD_SUMMARY_MAX = 6;

/**
 * One-line breakdown of the materials hold for the 🧺 chip's tap-toast: top items by count
 * (deterministic: count desc, id asc tiebreak), icon + name + ×count, overflow folded.
 */
export function holdSummaryText(hold: GoodsEntry[], maxItems: number = HOLD_SUMMARY_MAX): string {
  const rows = hold.filter((e) => e.count >= 1).sort((a, b) => b.count - a.count || a.id - b.id);
  if (!rows.length) return HOLD_CHIP_EMPTY;
  const shown = rows.slice(0, maxItems).map((e) => {
    const m = materialById(e.id);
    // This line is shown in a textContent toast — a raster path can't render, so fall back to a glyph.
    return `${m ? itemIconText(m.icon) : '📦'} ${m?.name ?? 'Goods'} ×${Math.floor(e.count)}`;
  });
  const extra = rows.length - maxItems;
  return `🧺 In the hold: ${shown.join(' · ')}${extra > 0 ? ` · +${extra} more` : ''} — bank at your home dock.`;
}
